import React from 'react';
import PropTypes from 'prop-types';
import {
  Flex, Text, Box, ActionNote,
} from '../components';
import { Button, SecondaryButton } from '../styles';
import AttestationCheckbox from './AttestationCheckbox';
import { acknowledgeNotice } from './noticeApi';

export const LANGUAGE_ORDER = ['en', 'es'];

export const UI_TEXT = {
  en: {
    stepLabel: 'Before you start',
    heading: 'A notice from your employer',
    intro: 'Your employer is required to give you this notice. Please read and acknowledge it before you fill out Form I-9.',
    languageToggle: 'Language',
    continueButton: 'Acknowledge and continue',
    requiredError: 'Required before continuing',
    submitError: 'Something went wrong while saving your acknowledgment. Please try again.',
  },
  es: {
    stepLabel: 'Antes de comenzar',
    heading: 'Un aviso de su empleador',
    intro: 'Su empleador está obligado a entregarle este aviso. Léalo y confírmelo antes de completar el Formulario I-9.',
    languageToggle: 'Idioma',
    continueButton: 'Confirmar y continuar',
    requiredError: 'Obligatorio para continuar',
    submitError: 'Ocurrió un error al guardar su confirmación. Inténtelo de nuevo.',
  },
};

// Heading and intro per notice key; UI_TEXT is the fallback for notices without one.
export const NOTICE_UI_TEXT = {
  co_id_document_notice: {
    en: {
      heading: 'Your ID document rights',
      intro: 'Colorado law requires you to read and acknowledge this notice before you fill out Form I-9.',
    },
    es: {
      heading: 'Sus derechos sobre sus documentos de identificación',
      intro: 'La ley de Colorado le exige leer y confirmar este aviso antes de completar el Formulario I-9.',
    },
  },
};

export function availableLanguages(notice) {
  const copy = (notice && notice.copy) || {};
  return LANGUAGE_ORDER.filter((lang) => copy[lang]);
}

export function defaultLanguage(notice) {
  const languages = availableLanguages(notice);
  const known = notice && notice.known_language;
  if (known && languages.includes(known)) return known;
  return languages[0] || 'en';
}

const stepLabelStyle = { textTransform: 'uppercase', letterSpacing: '0.04em' };

const noticeCardStyle = {
  border: '1px solid #E3E4E6',
  borderRadius: '8px',
  padding: '16px',
  maxHeight: '440px',
  overflowY: 'auto',
};

class NoticeStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      language: defaultLanguage(props.notice),
      checked: false,
      showRequiredError: false,
      submitting: false,
      submitError: false,
    };
  }

  handleContinue() {
    const { notice, digestKey, onAcknowledged } = this.props;
    const { checked, language, submitting } = this.state;
    if (submitting) return;
    if (!checked) {
      this.setState({ showRequiredError: true });
      return;
    }

    this.setState({ submitting: true, submitError: false });
    acknowledgeNotice({
      digestKey,
      noticeKey: notice.key,
      language,
      noticeVersion: notice.notice_version,
    })
      .then((response) => {
        this.setState({ submitting: false });
        onAcknowledged((response && response.data) || { key: notice.key });
      })
      .catch(() => {
        this.setState({ submitting: false, submitError: true });
      });
  }

  text() {
    const { notice } = this.props;
    const { language } = this.state;
    const perNotice = (NOTICE_UI_TEXT[notice.key] || {})[language] || {};
    return { ...(UI_TEXT[language] || UI_TEXT.en), ...perNotice };
  }

  copy() {
    const { notice } = this.props;
    const { language } = this.state;
    return (notice.copy && notice.copy[language]) || {};
  }

  renderLanguageToggle() {
    const { notice } = this.props;
    const { language } = this.state;
    const languages = availableLanguages(notice);
    if (languages.length < 2) return null;

    return (
      <Flex role="group" aria-label={this.text().languageToggle} style={{ flex: '0 0 auto', marginLeft: '16px' }}>
        {languages.map((lang, index) => {
          const ToggleButton = language === lang ? Button : SecondaryButton;
          return (
            <ToggleButton
              key={lang}
              type="button"
              aria-pressed={language === lang}
              style={{ marginLeft: index ? '8px' : 0 }}
              onClick={() => this.setState({ language: lang })}
            >
              {lang.toUpperCase()}
            </ToggleButton>
          );
        })}
      </Flex>
    );
  }

  render() {
    const { disabled, disabledText } = this.props;
    const {
      checked, showRequiredError, submitting, submitError,
    } = this.state;
    const text = this.text();
    const copy = this.copy();
    const paragraphs = copy.paragraphs || [];

    return (
      <Flex column data-testid="i9-notice-step">
        <Text fontSize="12px" color="#787883" fontWeight="600" style={stepLabelStyle}>
          {text.stepLabel}
        </Text>
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Text fontSize="22px" bold>
            {text.heading}
          </Text>
          {this.renderLanguageToggle()}
        </Flex>
        <Text fontSize="14px">
          {text.intro}
        </Text>

        <Box mt="24px" style={noticeCardStyle}>
          <Text fontSize="14px" fontWeight="600" lineHeight="20px">
            {copy.title}
          </Text>
          {copy.cite && (
            <Text fontSize="12px" color="#787883" mt="4px">
              {copy.cite}
            </Text>
          )}
          <Flex column mt="12px">
            {paragraphs.map((paragraph, index) => (
              // Paragraph order is fixed by the notice version, so the index is a stable key.
              // eslint-disable-next-line react/no-array-index-key
              <Text key={index} fontSize="14px" lineHeight="22px" mb="12px">
                {paragraph}
              </Text>
            ))}
          </Flex>
        </Box>

        <Box mt="16px">
          <AttestationCheckbox
            id="noticeAcknowledgmentCheck"
            name="noticeAcknowledgmentCheckbox"
            checked={checked}
            error={showRequiredError}
            errorText={text.requiredError}
            onChange={(value) => this.setState({
              checked: value,
              showRequiredError: value ? false : showRequiredError,
            })}
          >
            {copy.acknowledgment}
          </AttestationCheckbox>
        </Box>

        <Box mt="24px" />
        <Flex flexDirection="column">
          <Button
            disabled={disabled}
            loading={submitting ? 'true' : undefined}
            onClick={() => this.handleContinue()}
          >
            {text.continueButton}
          </Button>
          <Flex justifyContent="flex-end">
            {disabled && <ActionNote text={disabledText} />}
          </Flex>
        </Flex>
        {submitError && (
          <Flex justifyContent="flex-end" mt="4px">
            <Text color="red">{text.submitError}</Text>
          </Flex>
        )}
      </Flex>
    );
  }
}

export default NoticeStep;

const copyShape = PropTypes.shape({
  title: PropTypes.string,
  cite: PropTypes.string,
  paragraphs: PropTypes.arrayOf(PropTypes.string),
  acknowledgment: PropTypes.string,
});

NoticeStep.propTypes = {
  notice: PropTypes.shape({
    key: PropTypes.string.isRequired,
    notice_version: PropTypes.string.isRequired,
    known_language: PropTypes.string,
    copy: PropTypes.shape({ en: copyShape, es: copyShape }),
  }).isRequired,
  digestKey: PropTypes.string.isRequired,
  onAcknowledged: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledText: PropTypes.string,
};

NoticeStep.defaultProps = {
  disabled: false,
  disabledText: undefined,
};
